// DUPLICATE - pending removal
//
// This page used to be the standalone cover picker reached at
// /editor/:id, /editor/:id/:idx, and /editor/:id/:idx/covers. The
// wizard's StepDesign (in AlbumFlowPage) now owns first-time cover
// selection, and the editor's "Change cover" action will move into
// an in-editor modal in Phase 3. The first two routes now redirect
// to /flow. The /covers route stays mounted only until the editor
// modal lands; do NOT add new entry points here.
import { useRef, useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { carryCoverElements } from '@/lib/coverElements';
import { useQuery } from '@tanstack/react-query';
import { coversApi, ordersApi, sessionsApi, type Cover } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { coverCategoryFor } from '@/lib/coverCategoryMatch';
import { CoverThumbnail } from '@/components/CoverThumbnail';
import type { TextZoneOverride, ExtraTextZone } from '@/components/CoverThumbnail';
import { CoverPreviewDialog } from './CoverPreviewDialog';
import { cn } from '@/lib/utils';
import { titlePrefill } from '@/lib/titleSource';

type SortOption = 'popular' | 'alpha_az' | 'alpha_za' | 'newest' | 'oldest';

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'popular', label: 'Populaires' },
  { value: 'alpha_az', label: 'A → Z' },
  { value: 'alpha_za', label: 'Z → A' },
  { value: 'newest', label: 'Plus récents' },
  { value: 'oldest', label: 'Plus anciens' },
];

function getSavedSort(): SortOption {
  try {
    const saved = localStorage.getItem('storiz_cover_sort');
    if (saved && SORT_OPTIONS.some((o) => o.value === saved)) return saved as SortOption;
  } catch {}
  return 'popular';
}

export default function CoverLibraryPage() {
  const { shopifyOrderId, albumIndex } = useParams<{
    shopifyOrderId: string;
    albumIndex: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [pickDone, setPickDone] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [previewCover, setPreviewCover] = useState<Cover | null>(null);
  /**
   * PICK MODE ONLY — the cover the customer has tapped but not yet confirmed.
   *
   * ⚠️ SELECTION AND CONFIRMATION ARE NOW TWO SEPARATE ACTS. Tapping used to
   * open a dialog on top of the catalogue, which meant a customer choosing
   * between two covers had to open, close and reopen to compare them. Selecting
   * in place leaves the whole grid visible and puts one button underneath.
   */
  const [chosenCover, setChosenCover] = useState<Cover | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>(getSavedSort);

  useEffect(() => {
    document.title = 'Choisir une couverture — Storiz';
  }, []);

  // Persist sort preference
  useEffect(() => {
    try {
      localStorage.setItem('storiz_cover_sort', sortBy);
    } catch {}
  }, [sortBy]);

  const { data: orderData } = useQuery({
    queryKey: ['order', shopifyOrderId],
    queryFn: () => ordersApi.get(shopifyOrderId!),
    enabled: !!shopifyOrderId,
  });

  // If a cover is already chosen for this album, skip the picker and jump
  // straight into the editor. Without this, clicking "Edit album" from admin
  // (or revisiting via direct URL) reopens the library — and any subsequent
  // re-confirm in the dialog wipes back-cover photos / spine config / zone
  // overrides because the confirm handler always writes fresh defaults.
  const idxForRedirect = parseInt(albumIndex ?? '1', 10);
  const { data: sessionData, isLoading: sessionLoading } = useQuery({
    queryKey: ['session', shopifyOrderId, idxForRedirect],
    queryFn: () => sessionsApi.get(shopifyOrderId!, idxForRedirect),
    enabled: !!shopifyOrderId,
    staleTime: 0,
  });
  // CLIENT PICK MODE (2026-08-12) — `?pick=1`.
  //
  // The redirect below exists to stop the internal editor route reopening the
  // picker (a re-confirm rewrites spine + back-cover defaults). But the link we
  // send the client over WhatsApp lands on orders where the TEAM has usually
  // already set a cover, so that same redirect would bounce every client
  // straight out of the catalogue. In pick mode the client is exactly who we
  // want choosing, so the bounce is off and re-picking is allowed.
  const pickMode = new URLSearchParams(location.search).get('pick') === '1';

  useEffect(() => {
    if (!pickMode && sessionData?.session?.cover_id) {
      navigate(`/editor/${shopifyOrderId}/${albumIndex ?? '1'}/edit`, { replace: true });
    }
  }, [pickMode, sessionData?.session?.cover_id, shopifyOrderId, albumIndex, navigate]);

  const { data: categoriesData } = useQuery({
    queryKey: ['cover-categories'],
    queryFn: () => coversApi.categories(),
  });

  const { data: coversData, isLoading } = useQuery({
    queryKey: ['covers', selectedCategory],
    queryFn: () =>
      coversApi.list({
        product_type: 'travel',
        ...(selectedCategory ? { category: selectedCategory } : {}),
      }),
  });

  const order = orderData?.order;
  const categories = categoriesData?.categories ?? [];
  const rawCovers = coversData?.covers ?? [];

  // Calculate which destination we're editing
  const index = parseInt(albumIndex ?? '1', 10);
  const destination =
    index === 1
      ? order?.destination_1
      : index === 2
      ? order?.destination_2
      : order?.destination_3;
  const year =
    index === 1 ? order?.year_1 : index === 2 ? order?.year_2 : order?.year_3;
  // ⚠️ TWO DIFFERENT WORDS (2026-10-06, lib/titleSource.ts). The words on the
  // cover are the ALBUM's title — after a title change in the chat the order's
  // copy can still hold the old one, and this used to write it back on every
  // pick, front and spine. The catalogue search starts from the OCCASION.
  const titleNow = titlePrefill(sessionData?.session, destination);
  const occasion = sessionData?.session?.destination || destination;

  // Filter by search (client-side, case/accent insensitive)
  const filteredCovers = useMemo(() => {
    if (!searchQuery.trim()) return rawCovers;
    const q = normalizeSearch(searchQuery);
    return rawCovers.filter((cover) => {
      const haystack = normalizeSearch(
        [cover.name, ...(cover.categories ?? []), cover.description, ...(cover.search_tags ?? [])].join(' ')
      );
      return haystack.includes(q);
    });
  }, [rawCovers, searchQuery]);

  // Sort
  const sortedCovers = useMemo(() => {
    const arr = [...filteredCovers];
    switch (sortBy) {
      case 'alpha_az':
        return arr.sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      case 'alpha_za':
        return arr.sort((a, b) => b.name.localeCompare(a.name, 'fr'));
      case 'newest':
        return arr.sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
      case 'oldest':
        return arr.sort(
          (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      case 'popular':
      default:
        return arr.sort((a, b) => (b.usage_count ?? 0) - (a.usage_count ?? 0));
    }
  }, [filteredCovers, sortBy]);

  // ─── THE COVER WE ALREADY MATCHED GOES FIRST ───────────────────────────────
  //
  // The assistant matches a cover to the album's destination before it ever
  // sends the link — "MY LOVE" for a Mariage album, on 2026-08-16. But the
  // catalogue sorted by popularity, so the client landed on Agadir, Italie,
  // Korea and Iceland and reported the matching as broken. It was not: the
  // match was simply invisible, which for a client is the same thing.
  //
  // Only in pick mode. Inside the editor the team is browsing the whole
  // catalogue on purpose and a pinned card would just be in the way.
  // ─── OPEN ON THE COVERS THAT ARE ACTUALLY RELEVANT ─────────────────────────
  //
  // 2026-08-17: an album called "Cadeau pour sa femme / Couple" opened on 217
  // travel covers sorted by popularity and left the client to find the five
  // wedding ones himself. We already know what the album is about — the
  // customer told the assistant and it is on `destination_1`.
  //
  //   an OCCASION ("Couple", "3ers", "naissance")  → open on that category
  //   a PLACE ("New York")                         → search for it by name
  //
  // Applied ONCE, and only in pick mode: after that the filter belongs to the
  // client. `autoFocused` is a ref, not state — re-running on every render
  // would fight them every time they cleared the box.
  //
  // ⚠️ ONCE PER ALBUM, NOT ONCE PER PAGE LOAD. This was a plain boolean ref, and
  // walking a Duo from album 1 to album 2 is a client-side navigation that never
  // unmounts the component — so the ref stayed true, the filter stayed on
  // "Bali", and album 2's own suggested cover was filtered out of the grid. The
  // cover was matched and written; the customer simply could not see it. Keyed
  // by album index, each album focuses once and the client still owns the filter
  // afterwards.
  const autoFocused = useRef<string | null>(null);
  useEffect(() => {
    if (!pickMode || !occasion) return;
    const key = String(index);
    if (autoFocused.current === key) return;
    autoFocused.current = key;
    const cat = coverCategoryFor(occasion);
    // Set BOTH every time: leaving the previous album's search in place while
    // setting this album's category filters twice and shows nothing.
    if (cat) { setSelectedCategory(cat); setSearchQuery(''); }
    else { setSearchQuery(occasion); setSelectedCategory(''); }
  }, [pickMode, occasion, index]);

  const matchedCoverId = pickMode ? sessionData?.session?.cover_id ?? null : null;
  const totalAlbums = order?.bundle === 'Trio' ? 3 : order?.bundle === 'Duo' ? 2 : 1;
  const currentAlbumNo = parseInt(albumIndex ?? '1', 10);
  const isLastAlbum = currentAlbumNo >= totalAlbums;
  const orderedCovers = useMemo(() => {
    if (!matchedCoverId) return sortedCovers;
    const hit = sortedCovers.find((c) => c.id === matchedCoverId);
    if (!hit) return sortedCovers;
    return [hit, ...sortedCovers.filter((c) => c.id !== matchedCoverId)];
  }, [sortedCovers, matchedCoverId]);

  // ⚠️ NEVER LEAVE THEM ON AN EMPTY CATALOGUE. Our guess can be wrong — a
  // destination with no cover of its own, an occasion whose category is empty
  // — and a client staring at "no results" does not know WE narrowed it. If
  // the auto-focus produced nothing, undo it and show everything.
  useEffect(() => {
    if (!pickMode || isLoading || !autoFocused.current) return;
    if (orderedCovers.length > 0) return;
    if (searchQuery) setSearchQuery('');
    else if (selectedCategory) setSelectedCategory('');
  }, [pickMode, isLoading, orderedCovers.length, searchQuery, selectedCategory]);

  /**
   * Save this cover on the current album, then move on.
   *
   * ⚠️ ONE SAVE PATH, TWO CALLERS. The dialog (editor mode) passes the text the
   * customer typed; the pick-mode bar passes nothing and takes the defaults.
   * Two copies of this would be two places to forget the Duo/Trio advance.
   */
  const commitCover = async (cover: Cover, data?: {
    destinationText: string;
    yearText: string;
    zoneOverrides: Record<number, TextZoneOverride>;
    extraZones: ExtraTextZone[];
    customTexts?: Record<string, string>;
  }) => {
    const destinationText = data?.destinationText ?? titleNow;
    const yearText = data?.yearText ?? year ?? '';
    try {
      const albumIdx = parseInt(albumIndex ?? '1', 10);
      await sessionsApi.upsert(shopifyOrderId!, albumIdx, {
        cover_id: cover.id,
        cover_destination_text: destinationText || null,
        cover_year_text: yearText || null,
        // THE COVER IS SACRED: stickers / photos the team placed on this album's
        // cover survive a new cover pick (lib/coverElements.ts).
        cover_customizations: carryCoverElements(
          albumIdx === idxForRedirect ? sessionData?.session?.cover_customizations : null, {
          zoneOverrides: data?.zoneOverrides ?? {},
          extraZones: data?.extraZones ?? [],
          customTexts: data?.customTexts ?? {},
          backCoverBgColor: cover.dominant_color || null,
          backCoverImages: [],
          spineConfig: {
            // The spine is the same title, edge on — and empty for « no title ».
            text: destinationText,
            year: yearText || year || '',
            bgColor: cover.dominant_color || '#D35400',
            textColor:
              cover.text_zones?.find((z) => z.type === 'destination')?.color
              ?? cover.text_zones?.find((z) => z.type === 'year')?.color
              ?? '#FFFFFF',
            yearColor:
              cover.text_zones?.find((z) => z.type === 'year')?.color ?? '#C9A24A',
            fontSize: 28,
            yearFontSize: 14,
            rotation: 0,
            scale: 1,
          },
        }),
      });
    } catch (e) {
      console.warn('[CoverLibrary] Failed to save cover selection:', e);
    }
    setPreviewCover(null);
    setChosenCover(null);

    // CLIENT PICK MODE — the client never goes to the editor. On a Duo/Trio,
    // walk them to the next album; after the last one, show the done screen.
    if (pickMode) {
      const total = order?.bundle === 'Trio' ? 3 : order?.bundle === 'Duo' ? 2 : 1;
      const current = parseInt(albumIndex ?? '1', 10);
      if (current < total) {
        // ⚠️ A REAL NAVIGATION, so `albumIndex` changes and the page re-reads
        // destination_2 / destination_3 AND that album's own auto-matched
        // cover — which is what puts « ✦ Notre suggestion » on album 2 and 3.
        navigate(`/editor/${shopifyOrderId}/${current + 1}/covers?pick=1`);
        window.scrollTo({ top: 0 });
      } else {
        setPickDone(true);
      }
      return;
    }
    navigate(`/editor/${shopifyOrderId}/${albumIndex ?? '1'}/edit`);
  };

  // Done — every album has its cover. The client never sees the editor.
  if (pickDone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
        <div className="max-w-sm w-full text-center">
          <div className="text-5xl mb-4" aria-hidden>✓</div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            C’est enregistré, merci !
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Votre couverture est bien reçue. On prépare votre album et on vous
            envoie l’aperçu très vite.
          </p>
          {/* A web page cannot close its own tab — browsers block it. Sending
              them back to the conversation is more useful than a dead end. */}
          <a
            href="https://wa.me/212620066907"
            className="inline-block px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
            style={{ background: '#25D366' }}
          >
            Revenir sur WhatsApp
          </a>
        </div>
      </div>
    );
  }

  // Don't flash the picker while the session lookup is in flight or while
  // the redirect to /edit is being performed. In pick mode an existing cover
  // is NOT a reason to hide the catalogue — the client is here to change it.
  if (sessionLoading || (!pickMode && sessionData?.session?.cover_id)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // Anonymous customers hit ProtectedRoute → /login if we
              // navigate('/'). Send them back to the wizard if there's
              // an order id, otherwise to the storefront.
              if (shopifyOrderId) {
                navigate(`/flow/${shopifyOrderId}`);
              } else {
                window.location.href = 'https://storiz.ma';
              }
            }}
          >
            ← Retour
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-gray-900 truncate">
              {order?.shopify_order_name ?? 'Album'}
              {order?.bundle && order.bundle !== 'Solo' && (
                <span className="ml-2 text-sm text-gray-500">
                  Album {index}/{order.bundle === 'Duo' ? 2 : 3}
                </span>
              )}
            </h1>
            {(titleNow || occasion) && (
              <p className="text-xs text-gray-500 truncate">
                {titleNow || occasion} {year && `· ${year}`}
              </p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            Choisissez votre couverture
          </h2>
          <p className="text-gray-500 text-sm">
            Parcourez notre collection et sélectionnez le design qui vous inspire
          </p>
        </div>

        {/* Search bar + Sort dropdown */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un design..."
              className="w-full pl-10 pr-9 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-3 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer min-w-[140px]"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Category tabs */}
        <div
          className={cn(
            'bg-white rounded-xl border border-gray-200 p-2 mb-5 flex gap-1 overflow-x-auto',
            searchQuery.trim() ? 'opacity-60' : ''
          )}
        >
          <CategoryTab
            active={!selectedCategory}
            onClick={() => setSelectedCategory('')}
          >
            Toutes
          </CategoryTab>
          {categories.map((cat) => (
            <CategoryTab
              key={cat.slug}
              active={selectedCategory === cat.slug}
              onClick={() => setSelectedCategory(cat.slug)}
            >
              {cat.icon && <span className="mr-1">{cat.icon}</span>}
              {cat.label_fr}
            </CategoryTab>
          ))}
        </div>

        {/* Covers grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-white rounded-xl border border-gray-100 animate-pulse"
              />
            ))}
          </div>
        ) : sortedCovers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="text-5xl mb-3">🔍</div>
            <p className="text-gray-500 mb-2">
              {searchQuery.trim()
                ? `Aucun design trouvé pour "${searchQuery}"`
                : 'Aucune couverture disponible dans cette catégorie'}
            </p>
            {searchQuery.trim() && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-brand-500 text-sm hover:underline"
              >
                Effacer la recherche
              </button>
            )}
          </div>
        ) : (
          <>
            {searchQuery.trim() && (
              <p className="text-xs text-gray-400 mb-3">
                {sortedCovers.length} résultat{sortedCovers.length !== 1 ? 's' : ''} pour
                "{searchQuery}"
              </p>
            )}
            <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 ${pickMode && chosenCover ? "pb-24" : ""}`}>
              {orderedCovers.map((cover) => {
                const isMatch = cover.id === matchedCoverId;
                const isChosen = pickMode && chosenCover?.id === cover.id;
                return (
                  <button
                    key={cover.id}
                    onClick={() => (pickMode ? setChosenCover(cover) : setPreviewCover(cover))}
                    aria-pressed={pickMode ? isChosen : undefined}
                    data-testid={`cover-tile-${cover.id}`}
                    data-chosen={pickMode && isChosen ? 'true' : undefined}
                    data-suggested={isMatch ? 'true' : undefined}
                    className={`group relative bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all text-left border ${
                      isChosen
                        ? 'border-brand-600 ring-4 ring-brand-500/40 shadow-lg'
                        : isMatch
                        ? 'border-brand-500 ring-2 ring-brand-500/30 shadow-md'
                        : 'border-gray-100 hover:border-brand-500/30'
                    }`}
                  >
                    {isMatch && !isChosen && (
                      <span className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-brand-500 text-white text-[11px] font-semibold shadow">
                        ✦ Notre suggestion
                      </span>
                    )}
                    {isChosen && (
                      <span className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-brand-600 text-white text-[11px] font-semibold shadow">
                        ✓ Sélectionnée
                      </span>
                    )}
                    <CoverThumbnail
                      cover={cover}
                      className="group-hover:scale-[1.02] transition-transform"
                    />
                    <div className="p-3">
                      <p className="font-medium text-gray-900 text-sm truncate">
                        {cover.name}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* ── The confirm step, in place of the popup ──────────────────────
          ⚠️ STICKY, NOT AT THE PAGE FOOT. The catalogue is several screens
          long; a button rendered after the last tile is a button a customer
          on album 2 never scrolls to. It sits above the fold of whatever they
          are looking at, and only exists once they have actually chosen. */}
      {pickMode && chosenCover && (
        <div
          className="fixed bottom-0 left-0 right-0 z-30 border-t border-gray-200 bg-white/95 backdrop-blur
                     px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]"
          data-testid="cover-confirm-bar"
        >
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-gray-900 truncate">
                {chosenCover.name}
              </p>
              <p className="text-[11.5px] text-gray-500">
                {isLastAlbum
                  ? 'Dernière couverture — on lance le montage après.'
                  : `Album ${currentAlbumNo} sur ${totalAlbums} · on passe au suivant après.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setChosenCover(null)}
              className="text-[12.5px] text-gray-500 underline shrink-0 cursor-pointer"
            >
              Changer
            </button>
            <button
              type="button"
              data-testid="cover-confirm"
              onClick={() => commitCover(chosenCover)}
              className="shrink-0 rounded-xl bg-brand-500 px-5 py-2.5 text-white text-[13px] font-semibold
                         hover:bg-brand-600 transition-colors cursor-pointer"
            >
              {isLastAlbum ? 'Confirmer' : 'Continuer'}
            </button>
          </div>
        </div>
      )}

      {previewCover && order && !pickMode && (
        <CoverPreviewDialog
          cover={previewCover}
          destination={titleNow}
          year={year ?? ''}
          onClose={() => setPreviewCover(null)}
          simple={pickMode}
          onConfirm={(data: {
            destinationText: string;
            yearText: string;
            zoneOverrides: Record<number, TextZoneOverride>;
            extraZones: ExtraTextZone[];
            customTexts?: Record<string, string>;
          }) => commitCover(previewCover, data)}
        />
      )}

    </div>
  );
}

function CategoryTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
        active ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-50'
      )}
    >
      {children}
    </button>
  );
}

/**
 * Normalize a string for accent-insensitive, case-insensitive search
 */
function normalizeSearch(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
